import { GraphqlError } from 'core/utils/backend-service';

const LOT_VALIDATION_RULES_IMMUTABLE_CODE = 'MATERIAL_DEFINITION_VALIDATION_RULES_IMMUTABLE';
const RESULT_TYPE_IMMUTABLE_CODE = 'MATERIAL_RESULT_TYPE_IMMUTABLE_ONCE_LOTS_EXIST';
const RANGE_MODE_IMMUTABLE_CODE = 'MATERIAL_RANGE_MODE_IMMUTABLE_ONCE_LOTS_EXIST';
// Backend returns HTTP 409 with this code (nested under extensions.code) when the material still has lots assigned.
const MATERIAL_IN_USE_CODE = 'E_POC_AMM_MATERIAL_IN_USE';
const HTTP_CONFLICT = 409;

const QC_VALIDATION_CODES = [
    'MATERIAL_QC_LEVELS_AT_LEAST_ONE_REQUIRED',
    'MATERIAL_QC_LEVELS_DUPLICATE_NAME',
    'MATERIAL_QC_LEVELS_MAX_EXCEEDED',
    'MATERIAL_QC_RANGE_MIN_GREATER_THAN_MAX',
    'MATERIAL_QC_RANGE_INVALID_DEVIATION',
    'MATERIAL_QC_RANGE_MODE_MISMATCH',
    'MATERIAL_QC_RANGE_INCOMPLETE',
    'MATERIAL_POSSIBLE_VALUES_DUPLICATE',
    'MATERIAL_POSSIBLE_VALUES_NOT_QUALITATIVE',
    'MATERIAL_TARGET_VALUES_NOT_IN_POSSIBLE_VALUES',
    'MATERIAL_TARGET_VALUES_NO_POSSIBLE_VALUES',
];

export type MaterialDefinitionApiErrorKind =
    | 'immutable-rules'
    | 'result-type-immutable'
    | 'range-mode-immutable'
    | 'qc-validation'
    | 'in-use'
    | 'duplicate'
    | 'unknown';

// The AMM gateway nests each backend code under `extensions.code` (see toGatewayErrors in
// core/utils/backend-service), not at the top level.
const gatewayCodesOf = (error: Error): string[] => {
    try {
        const parsed = JSON.parse(error.message) as { errors?: GraphqlError[] };
        return parsed.errors?.flatMap((entry) => (entry.extensions?.code == null ? [] : [entry.extensions.code])) ?? [];
    } catch {
        return [];
    }
};

const matchesGatewayCode = (error: Error, code: string): boolean => gatewayCodesOf(error).includes(code);

const matchesAnyGatewayCode = (error: Error, codes: string[]): boolean =>
    gatewayCodesOf(error).some((code) => codes.includes(code));

export const classifyMaterialDefinitionApiError = (error: unknown): MaterialDefinitionApiErrorKind => {
    if (!(error instanceof Error)) {
        return 'unknown';
    }
    if (matchesGatewayCode(error, LOT_VALIDATION_RULES_IMMUTABLE_CODE)) {
        return 'immutable-rules';
    }
    if (matchesGatewayCode(error, RESULT_TYPE_IMMUTABLE_CODE)) {
        return 'result-type-immutable';
    }
    if (matchesGatewayCode(error, RANGE_MODE_IMMUTABLE_CODE)) {
        return 'range-mode-immutable';
    }
    if (matchesAnyGatewayCode(error, QC_VALIDATION_CODES)) {
        return 'qc-validation';
    }
    if (matchesGatewayCode(error, MATERIAL_IN_USE_CODE)) {
        return 'in-use';
    }
    if ((error as Error & { status?: number }).status === HTTP_CONFLICT) {
        return 'duplicate';
    }
    return 'unknown';
};
